import { useState, useEffect } from "react";
import { supabase } from "./lib/supabase";

const ADMIN_EMAIL = "beaumariage@wedyra.fr";

const T = {
  primary: "#B5647A", primaryBg: "#FAF0F3", primaryBorder: "#EBC5CE",
  page: "#FDFAF9", card: "#FFFFFF", cardBorder: "#F0E8EC",
  header: "#FFFFFF", headerBorder: "#F0E8EC",
  text: "#2C1A22", textMuted: "#7A5A65", textLight: "#B8A0A8",
  green: "#4E8C65", greenBg: "#EBF5EF",
  blue: "#4A729A", blueBg: "#EBF2F7",
  amber: "#B8803A", amberBg: "#FBF0E3",
};

const badge = (label, bg, color) => (
  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 100, background: bg, color, letterSpacing: "0.5px" }}>
    {label}
  </span>
);

const PLAN_BADGE = {
  trial:     () => badge("Trial",     T.amberBg,  T.amber),
  essentiel: () => badge("Essentiel", T.blueBg,   T.blue),
  premium:   () => badge("Premium",   T.primaryBg, T.primary),
  elite:     () => badge("Elite",     "#F0EBF8",  "#7B5EA7"),
};

function fmt(date) {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminDashboard({ user, onLogout, onImpersonate }) {
  const [tab, setTab] = useState(0); // 0=couples, 1=prestataires
  const [couples, setCouples] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: c }, { data: p }] = await Promise.all([
        supabase.from("couples").select("*").order("created_at", { ascending: false }),
        supabase.from("prestataires").select("*").order("created_at", { ascending: false }),
      ]);
      setCouples(c || []);
      setPros(p || []);
      setLoading(false);
    })();
  }, []);

  const filteredCouples = couples.filter(c =>
    `${c.prenom1} ${c.prenom2} ${c.email} ${c.ville || ""}`.toLowerCase().includes(search.toLowerCase())
  );
  const filteredPros = pros.filter(p =>
    `${p.nom_entreprise} ${p.email} ${p.ville || ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const headerStyle = {
    position: "sticky", top: 0, zIndex: 10,
    background: T.header, borderBottom: `1px solid ${T.headerBorder}`,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 24px", height: 60,
  };

  const tabStyle = (active) => ({
    padding: "8px 20px", borderRadius: 10, border: "none", cursor: "pointer",
    fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: active ? 600 : 400,
    background: active ? T.primaryBg : "transparent",
    color: active ? T.primary : T.textMuted,
    transition: "all 0.15s",
  });

  const colHead = { fontSize: 11, fontWeight: 600, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.8px", padding: "10px 16px" };
  const cell = { padding: "14px 16px", fontSize: 14, color: T.text, borderTop: `1px solid ${T.cardBorder}` };

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "'DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: T.text }}>Wedyra Admin</span>
          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 100, background: "#2C1A22", color: "white" }}>ADMIN</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: T.textMuted }}>{ADMIN_EMAIL}</span>
          <button onClick={onLogout} style={{ padding: "7px 16px", borderRadius: 10, border: `1px solid ${T.primaryBorder}`, background: "white", color: T.primary, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            Déconnexion
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 24px" }}>
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 32 }}>
          {[
            { label: "Couples inscrits", val: couples.length, bg: T.primaryBg, color: T.primary },
            { label: "Prestataires inscrits", val: pros.length, bg: T.blueBg, color: T.blue },
            { label: "Abonnés actifs", val: pros.filter(p => p.abonnement_actif).length, bg: T.greenBg, color: T.green },
            { label: "En trial", val: pros.filter(p => p.abonnement_plan === "trial").length, bg: T.amberBg, color: T.amber },
          ].map((s, i) => (
            <div key={i} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: "18px 20px" }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs + Search */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 4 }}>
            <button style={tabStyle(tab === 0)} onClick={() => { setTab(0); setSearch(""); }}>
              Couples ({couples.length})
            </button>
            <button style={tabStyle(tab === 1)} onClick={() => { setTab(1); setSearch(""); }}>
              Prestataires ({pros.length})
            </button>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher..."
            style={{ padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, fontFamily: "'DM Sans',sans-serif", fontSize: 14, outline: "none", color: T.text, width: 220 }}
          />
        </div>

        {/* Table */}
        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 16, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: T.textMuted }}>Chargement...</div>
          ) : tab === 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: T.primaryBg }}>
                <tr>
                  {["Couple", "Email", "Mariage", "Ville", "Inscrit le", ""].map((h, i) => (
                    <th key={i} style={{ ...colHead, textAlign: i === 5 ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCouples.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...cell, textAlign: "center", color: T.textMuted }}>Aucun résultat</td></tr>
                ) : filteredCouples.map(c => (
                  <tr key={c.id} style={{ transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.primaryBg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={cell}>
                      <span style={{ fontWeight: 600 }}>{c.prenom1} & {c.prenom2}</span>
                    </td>
                    <td style={{ ...cell, color: T.textMuted }}>{c.email}</td>
                    <td style={cell}>{fmt(c.date_mariage)}</td>
                    <td style={{ ...cell, color: T.textMuted }}>{c.ville || "—"}</td>
                    <td style={{ ...cell, color: T.textMuted }}>{fmt(c.created_at)}</td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      <button
                        onClick={() => onImpersonate({
                          id: c.id, nom: `${c.prenom1} & ${c.prenom2}`,
                          date: c.date_mariage, role: "couple", ville: c.ville,
                        })}
                        style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: T.primary, color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      >
                        Accéder →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ background: T.blueBg }}>
                <tr>
                  {["Entreprise", "Email", "Plan", "Ville", "Inscrit le", ""].map((h, i) => (
                    <th key={i} style={{ ...colHead, textAlign: i === 5 ? "right" : "left" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredPros.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...cell, textAlign: "center", color: T.textMuted }}>Aucun résultat</td></tr>
                ) : filteredPros.map(p => (
                  <tr key={p.id} style={{ transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.blueBg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <td style={cell}>
                      <span style={{ fontWeight: 600 }}>{p.nom_entreprise}</span>
                    </td>
                    <td style={{ ...cell, color: T.textMuted }}>{p.email}</td>
                    <td style={cell}>{(PLAN_BADGE[p.abonnement_plan] || (() => badge(p.abonnement_plan || "—", "#F5F5F5", "#999")))()}</td>
                    <td style={{ ...cell, color: T.textMuted }}>{p.ville || "—"}</td>
                    <td style={{ ...cell, color: T.textMuted }}>{fmt(p.created_at)}</td>
                    <td style={{ ...cell, textAlign: "right" }}>
                      <button
                        onClick={() => onImpersonate({
                          id: p.id, nom: p.nom_entreprise, role: "pro", data: p,
                        })}
                        style={{ padding: "6px 14px", borderRadius: 8, border: "none", background: T.blue, color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      >
                        Accéder →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
